<script lang="ts">
  import { Badge } from '$lib/components/ui/badge/index.js'
  import { Skeleton } from '$lib/components/ui/skeleton/index.js'
  import { Progress as Progression } from '$lib/components/ui/progress/index.js'
  import * as DropdownMenu from '$lib/components/ui/dropdown-menu/index.js'
  import EllipsisVertical from '@lucide/svelte/icons/ellipsis-vertical'
  import ServerIcon from '@lucide/svelte/icons/server'
  import LibraryIcon from '@lucide/svelte/icons/library'
  import { route } from '@mateothegreat/svelte5-router'
  import PLACEHOLDER from '$assets/icons/book/cover_placeholder.svg'

  import BAD from '$assets/icons/rating/bad.svg'
  import AVERAGE from '$assets/icons/rating/average.svg'
  import GOOD from '$assets/icons/rating/good.svg'

  import { servers } from '$lib/store'
  import { refreshSeriesMetadata, unmatchSeries } from '$lib/apis/series/api'
  import { trackOperation, operationTitle } from '$lib/operations.svelte'
  import FixMatch from '$lib/components/nuread/modals/FixMatch.svelte'
  import * as Tooltip from '$lib/components/ui/tooltip/index.js'
  import { lineClampStyle, linesThatFit, flexLeftoverHeight } from '$lib/utils'

  let { data: d } = $props<{
    data: {
      series: any
      dims: {
        height: number
        width: number
      }
      mediaId: string
      libraryId: string
      location?: {
        serverName?: string
        serverTag?: string
        libraryName?: string
      }
      cdnUrl: string
    }
  }>()

  const Infos = $derived(d.series?.Infos)
  const Status = $derived(d.series?.Status)
  const Dates = $derived(d.series?.Dates)
  const genres = $derived(d.series?.genres)
  const Score = $derived(d.series?.Score)
  const Progress = $derived(d.series?.Progress)
  const localTitle = $derived(d.series?.localTitle)
  const kind = $derived(d.series?.kind)
  const seriesId = $derived(d.series?.id)

  const isNew = $derived(Progress?.Completed == 0)
  const remaining = $derived(Progress?.Total - Progress?.Completed ?? 0)
  const progress = $derived(Math.min(100, 100 * (Progress?.Completed / Progress?.Total)))
  const displayName = $derived(Infos?.title || localTitle)
  const hasRemoteCover = $derived(!!Status?.hasCover)

  const remoteCoverSrc = $derived.by(() => {
    if (!hasRemoteCover) return ''
    const v = Infos?.refreshedAt ? new Date(Infos.refreshedAt).getTime() : 0
    const bust = v ? `?v=${v}` : ''
    let src = `${d.cdnUrl}/api/assets/cover/${seriesId}${bust}`
    if (d.cdnUrl.indexOf('http:') != -1) {
      const qs = [`upstream_addr=${d.cdnUrl.replace('http://', '')}`, v ? `v=${v}` : '']
        .filter(Boolean)
        .join('&')
      src = `https://proxy.nuread.direct.habysmol.com/api/assets/cover/${seriesId}?${qs}`
    }
    return src
  })

  const rating = $derived.by(() => {
    const mean = Score?.meanScore ?? 0
    if (mean == 0) return { value: 'NA' as string | number, icon: '' }
    if (mean < 50) return { value: mean, icon: BAD }
    if (mean < 75) return { value: mean, icon: AVERAGE }
    return { value: mean, icon: GOOD }
  })

  let loaded = $state(false)
  let failed = $state(false)
  let loading = $state(false)
  let showDetails = $state(false)
  let menuOpen = $state(false)
  let refreshing = $state(false)
  let unmatching = $state(false)
  let fixMatchOpen = $state(false)
  let descEl: HTMLElement | undefined = $state()
  let descClamp = $state(0)

  const useFallback = $derived(!hasRemoteCover || failed)
  const coverSrc = $derived(useFallback ? PLACEHOLDER : remoteCoverSrc)

  // Grid cell size stays in layout px; chrome (gaps, title band, overlay insets) in rem
  // so it tracks root font-size the same way text-sm does across browsers.
  const slotVars = $derived(
    `--slot-w:${d.dims.width}px;--slot-h:${d.dims.height}px`
  )
  const coverBoxStyle = $derived(
    `${slotVars};width:calc(var(--slot-w) - 1.875rem);height:calc(var(--slot-h) - 3.125rem)`
  )
  // cover − top-10 (2.5rem) − rating clearance (2.25rem)
  const detailsBoxStyle = $derived(
    `${slotVars};height:calc(var(--slot-h) - 3.125rem - 2.5rem - 2.25rem)`
  )

  $effect(() => {
    if (!hasRemoteCover) {
      loading = false
      loaded = true
      failed = false
      return
    }
    const src = remoteCoverSrc
    loading = true
    loaded = false
    failed = false
    const img = new Image()
    img.src = src
    img.onload = () => {
      loading = false
      loaded = true
    }
    img.onerror = () => {
      loading = false
      failed = true
      loaded = true
    }
  })

  // Fit ellipsis clamp to the flex leftover height (adapts to rem text-sm/text-xs).
  $effect(() => {
    if (!showDetails || !descEl) {
      descClamp = 0
      return
    }
    const el = descEl
    const sync = () => {
      descClamp = linesThatFit(el, flexLeftoverHeight(el))
    }
    const parent = el.parentElement
    const ro = new ResizeObserver(sync)
    ro.observe(parent ?? el)
    requestAnimationFrame(sync)
    return () => ro.disconnect()
  })

  async function onRefreshMetadata() {
    if (refreshing) return
    refreshing = true
    try {
      const server = $servers[d.mediaId]
      const data = await refreshSeriesMetadata(server, seriesId)
      trackOperation({
        server,
        operation: data?.operation ?? {},
        title: operationTitle(data?.operation?.type, 'Refreshing metadata'),
        description: displayName,
      })
    } catch (err) {
      console.error('refresh metadata failed', err)
    } finally {
      refreshing = false
    }
  }

  async function onUnmatch() {
    if (unmatching) return
    unmatching = true
    try {
      const server = $servers[d.mediaId]
      const data = await unmatchSeries(server, seriesId)
      trackOperation({
        server,
        operation: data?.operation ?? {},
        title: operationTitle(data?.operation?.type, 'Unmatching series'),
        description: displayName,
      })
    } catch (err) {
      console.error('unmatch failed', err)
    } finally {
      unmatching = false
    }
  }
</script>

<div
  class="relative transition-transform hover:scale-105"
  style="width: calc(var(--slot-w) - 1.875rem); {slotVars}"
  role="gridcell"
  onmouseenter={() => (showDetails = true)}
  onmouseleave={() => {
    if (!menuOpen) showDetails = false
  }}
>
  <a
    tabindex="0"
    class="block"
    href="/media/{d.mediaId}/library/{d.libraryId}/series/{seriesId}"
    use:route
  >
    {#if remaining > 0}
      <Badge
        class="absolute -top-2 -right-2 h-5 min-w-5 z-2 rounded-full px-1 bg-macchiato-mauve text-macchiato-base font-medium px-0 ring-2"
        variant="secondary"
      >
        {remaining}
      </Badge>
    {/if}
    {#if loading}
      <Skeleton
        style={coverBoxStyle}
        class="rounded-t"
      />
    {:else if loaded}
      <!-- Wrapper keeps rounded card edges sharp; blur applies only to real cover pixels. -->
      <div
        class="rounded-t overflow-hidden shadow-2xl"
        style={coverBoxStyle}
      >
        <img
          class="size-full object-cover dark:bg-macchiato-base
          {showDetails && !useFallback ? 'brightness-[0.33]' : ''}
          {Status?.isAdult && !useFallback ? 'blur-[20px] scale-[1.15]' : ''}"
          src={coverSrc}
          alt={displayName ? displayName : 'Book cover'}
        />
      </div>
    {/if}

    <div class="absolute w-full h-full top-1 overflow-hidden">
      <div class="flex flex-wrap gap-0.5 px-1 z-[2] mb-1">
        <Badge
          class="py-0 font-medium text-white dark:text-black bg-latte-mauve dark:bg-macchiato-mauve"
          >{kind.toLowerCase()}</Badge
        >
        {#if isNew}
          <Badge
            class="py-0 font-medium text-white dark:text-black bg-latte-green dark:bg-macchiato-green"
            >new</Badge
          >
        {/if}
        {#if Status?.isAdult}
          <Badge
            class="py-0 font-medium text-white dark:text-black bg-latte-red dark:bg-macchiato-red"
            >nfsw</Badge
          >
        {/if}
      </div>
    </div>

    {#if d.location?.serverName || d.location?.serverTag || d.location?.libraryName}
      <div
        class="absolute opacity-95 right-5 bottom-12 z-[2] flex max-w-[70%] flex-col gap-px rounded-md border border-latte-surface1/60 bg-latte-mantle/90 px-1.5 py-0.5 shadow-sm backdrop-blur-sm dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/90"
      >
        {#if d.location.serverName || d.location.serverTag}
          <Tooltip.Provider delayDuration={200}>
            <Tooltip.Root>
              <Tooltip.Trigger>
                {#snippet child({ props })}
                  <div
                    {...props}
                    class="flex min-w-0 cursor-default items-center gap-1"
                  >
                    <ServerIcon
                      class="size-3 shrink-0 text-latte-blue dark:text-macchiato-blue"
                      aria-hidden="true"
                    />
                    <p
                      class="truncate text-xs font-medium leading-tight text-latte-text dark:text-macchiato-text"
                    >
                      {d.location.serverName ?? '…'}
                    </p>
                  </div>
                {/snippet}
              </Tooltip.Trigger>
              {#if d.location.serverTag}
                <Tooltip.Content side="top" sideOffset={6} class="z-[60]">
                  #{d.location.serverTag}
                </Tooltip.Content>
              {/if}
            </Tooltip.Root>
          </Tooltip.Provider>
        {/if}
        {#if d.location.libraryName}
          <div class="flex min-w-0 items-center gap-1">
            <LibraryIcon
              class="size-3 shrink-0 text-latte-peach dark:text-macchiato-peach"
              aria-hidden="true"
            />
            <p class="truncate text-xs font-medium leading-tight text-latte-text dark:text-macchiato-text">
              {d.location.libraryName}
            </p>
          </div>
        {/if}
      </div>
    {/if}

    {#if showDetails}
      <!-- Bound to cover height; chrome insets in rem so they track text-sm across browsers. -->
      <div
        class="absolute inset-x-0 top-10 flex flex-col overflow-hidden px-2"
        style={detailsBoxStyle}
      >
        <div class="shrink-0 text-sm font-light leading-tight text-white text-ellipsis overflow-hidden">
          {Dates?.startDate?.year ?? ''}
        </div>
        <div class="shrink-0 text-lg font-medium leading-tight text-white line-clamp-2">
          {localTitle}
        </div>
        <div class="shrink-0 flex flex-wrap gap-0.5 mt-0.5">
          {#each (genres?.map(({ Name }) => Name) ?? []).slice(0, 4) as genre}
            <Badge
              class="py-0 font-medium text-white dark:text-black bg-latte-peach dark:bg-macchiato-peach overflow-hidden"
              >{genre}</Badge
            >
          {/each}
        </div>
        <div
          bind:this={descEl}
          class="min-h-0 flex-1 overflow-hidden text-gray-300 text-xs md:text-sm mt-2 leading-snug
            [&_p]:m-0 [&_p]:inline [&_br]:hidden [&_i]:italic [&_em]:italic [&_b]:font-bold [&_strong]:font-bold"
          style={lineClampStyle(descClamp)}
        >
          {@html Infos?.description ?? ''}
        </div>
      </div>
    {/if}
    <div
      class="flex row absolute bottom-10 h-7 w-15 opacity-75 bg-latte-crust dark:bg-macchiato-crust rounded-r-3xl"
    >
      {#if rating.icon}
        <img src={rating.icon} class="h-4 my-1.5 mx-1" alt="" />
      {/if}
      <div class="text-sm my-1">
        {rating.value}{rating.value === 'NA' ? '' : '%'}
      </div>
    </div>
    <Progression
      value={progress}
      max={100}
      class="w-[99.99%] absolute bottom-10 h-1 rounded-none bg-macchiato-crust"
      activeClass="bg-macchiato-mauve"
    />
    <h5
      id="title-{seriesId}"
      class="truncate text-md font-normal tracking-tight text-gray-900 dark:text-white"
    >
      {displayName}
    </h5>
    <p class="text-sm font-light text-gray-700 dark:text-gray-400 leading-tight">
      {Dates?.startDate?.year || '???'}
    </p>
  </a>

  <!-- Bottom-right of the cover: opposite the rating chip, outside the link so the menu doesn't navigate. -->
  <div class="absolute bottom-10 -right-2 z-20">
    <DropdownMenu.Root bind:open={menuOpen}>
      <DropdownMenu.Trigger
        class="flex h-[2.625rem] w-[2.625rem] items-center justify-center rounded-full
          bg-transparent text-white/70
          opacity-0 transition-all
          {showDetails || menuOpen ? 'opacity-100' : ''}
          hover:bg-transparent hover:text-white"
        aria-label="Series options"
      >
        <EllipsisVertical class="h-6 w-6 drop-shadow-md" />
      </DropdownMenu.Trigger>
      <DropdownMenu.Content class="w-48 opacity-95" align="start">
        <DropdownMenu.Group>
          <DropdownMenu.Item disabled>Mark as Read</DropdownMenu.Item>
          <DropdownMenu.Item disabled>Mark as Unread</DropdownMenu.Item>
          <DropdownMenu.Separator />
          <DropdownMenu.Item
            onclick={(e) => {
              e.preventDefault()
              menuOpen = false
              fixMatchOpen = true
            }}
          >
            Fix Match
          </DropdownMenu.Item>
          <DropdownMenu.Item
            disabled={unmatching}
            onclick={(e) => {
              e.preventDefault()
              onUnmatch()
            }}
          >
            {unmatching ? 'Unmatching…' : 'Unmatch'}
          </DropdownMenu.Item>
          <DropdownMenu.Item
            disabled={refreshing}
            onclick={(e) => {
              e.preventDefault()
              onRefreshMetadata()
            }}
          >
            {refreshing ? 'Refreshing…' : 'Refresh Metadata'}
          </DropdownMenu.Item>
        </DropdownMenu.Group>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  </div>
</div>

{#if fixMatchOpen}
  <FixMatch
    bind:open={fixMatchOpen}
    server={$servers[d.mediaId]}
    {seriesId}
    {localTitle}
  />
{/if}
